import { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from 'components/v2/Tooltip';
import { MONTH_NAMES } from 'itrvl-types';
import { useEdgeAvailability } from 'api/queries/edge';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isBefore,
  startOfDay,
  isSameMonth,
  addWeeks,
  getWeeksInMonth,
  addDays,
  parseISO,
} from 'date-fns';
import useDateRangeSelection from './useDateRangeSelection';
import { CELL_SIZE, WeekHeaderGrid, DayGrid, CellOuter, CalendarCell, EnquiryButton } from './EdgeAvailabilityCalendar.styles';

const DAYS = [
  { abbr: 'M', label: 'Monday' },
  { abbr: 'T', label: 'Tuesday' },
  { abbr: 'W', label: 'Wednesday' },
  { abbr: 'T', label: 'Thursday' },
  { abbr: 'F', label: 'Friday' },
  { abbr: 'S', label: 'Saturday' },
  { abbr: 'S', label: 'Sunday' },
];

function getTooltipText(status) {
  if (status === true) return 'Available';
  if (status === 'onRequest') return 'On Request';
  if (status === false) return 'Unavailable';
  return '';
}

export default function EdgeAvailabilityCalendar({
  savedItineraryId,
  selectedDate,
  onSelectDate,
  onRangeSelect,
  stayNights = 0,
  adults,
  childrenAges,
  initialMonth,
  skipAvailability = false,
  onRequestQuote,
  onAccommodationsChange,
}) {
  const currentDate = useMemo(() => initialMonth || new Date(), [initialMonth]);
  const [hoverDateSimple, setHoverDateSimple] = useState(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const { data, isLoading: queryLoading } = useEdgeAvailability(savedItineraryId, currentDate, { adults, childrenAges });

  const isLoading = !skipAvailability && queryLoading;
  const availability = useMemo(() => data?.availability || {}, [data?.availability]);

  useEffect(() => {
    if (data?.accommodations && onAccommodationsChange) {
      onAccommodationsChange(data.accommodations);
    }
  }, [data?.accommodations, onAccommodationsChange]);

  const noAvailability = useMemo(() => {
    if (skipAvailability || isLoading) return false;
    const today = startOfDay(new Date());
    const inMonthDates = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const futureDates = inMonthDates.filter(d => !isBefore(d, today));
    if (futureDates.length === 0) return false;
    return futureDates.every(d => {
      const key = format(d, 'yyyy-MM-dd');
      return availability[key] === false || availability[key] === undefined;
    });
  }, [skipAvailability, isLoading, availability, monthStart, monthEnd]);

  const { selectedDates, endDateString } = useMemo(() => {
    if (!selectedDate || stayNights <= 0)
      return { selectedDates: new Set(selectedDate ? [selectedDate] : []), endDateString: selectedDate };
    const start = parseISO(selectedDate);
    const end = addDays(start, stayNights);
    return {
      selectedDates: new Set(eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'))),
      endDateString: format(end, 'yyyy-MM-dd'),
    };
  }, [selectedDate, stayNights]);

  const range = useDateRangeSelection({ selectedDate, stayNights, onRangeSelect });
  const hoverDate = onRangeSelect ? range.hoverDate : hoverDateSimple;
  const hoverDates = onRangeSelect ? range.hoverDates : new Set();
  const hoverBefore = onRangeSelect ? range.hoverBefore : false;

  const days = useMemo(() => {
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const weeks = getWeeksInMonth(currentDate, { weekStartsOn: 1 });
    const delta = 6 - weeks;
    const end = addWeeks(endOfWeek(monthEnd, { weekStartsOn: 1 }), delta);
    return eachDayOfInterval({ start, end });
  }, [currentDate, monthStart, monthEnd]);

  const today = startOfDay(new Date());

  if (noAvailability) {
    const monthName = MONTH_NAMES[currentDate.getMonth()];
    const yearStr = currentDate.getFullYear();
    return (
      <Stack alignItems="center" gap={2} sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          No availability in {monthName} {yearStr}. Try a different month or request a custom quote.
        </Typography>
        {onRequestQuote && (
          <EnquiryButton variant="rounded" onClick={onRequestQuote}>
            Request A Custom Quote
          </EnquiryButton>
        )}
      </Stack>
    );
  }

  return (
    <Box role="group" aria-label="Availability calendar" data-edge-calendar data-test-id="availability-calendar">
      {isLoading && (
        <Stack alignItems="center" sx={{ mb: 1 }}>
          <CircularProgress size={14} color="secondary" />
        </Stack>
      )}

      <WeekHeaderGrid>
        {DAYS.map(({ abbr, label }) => (
          <Typography key={label} variant="caption" aria-label={label} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {abbr}
          </Typography>
        ))}
      </WeekHeaderGrid>

      <DayGrid>
        {days.map(day => {
          const dateString = format(day, 'yyyy-MM-dd');
          const inMonth = isSameMonth(day, currentDate);
          const isPast = isBefore(day, today);
          const isSelected = selectedDates.has(dateString);
          const isHovered = hoverDates.has(dateString);
          const status = availability[dateString];
          const hasData = status !== undefined;

          const isClickableDate = skipAvailability
            ? !isPast
            : inMonth && !isPast && (status === true || status === 'onRequest' || dateString === selectedDate);

          const isEndDate = dateString === endDateString;
          const isStartDate = dateString === selectedDate;
          let cellStatus;
          if (isSelected && (isStartDate || isEndDate)) {
            cellStatus = 'selected';
          } else if (isSelected) {
            cellStatus = 'selectedTween';
          } else if (isHovered && isClickableDate) {
            cellStatus = 'hoverPreview';
          } else if (!inMonth) {
            cellStatus = skipAvailability && !isPast ? 'available' : 'outOfMonth';
          } else if (isPast) {
            cellStatus = 'past';
          } else if (skipAvailability) {
            cellStatus = 'available';
          } else if (!hasData) {
            cellStatus = 'loading';
          } else if (status === true) {
            cellStatus = 'available';
          } else if (status === 'onRequest') {
            cellStatus = 'onRequest';
          } else {
            cellStatus = 'unavailable';
          }

          const isClickable = isClickableDate || isStartDate;

          const circle = <CalendarCell status={cellStatus}>{format(day, 'd')}</CalendarCell>;

          const inRange = isSelected && stayNights > 0;
          const inHoverRange = hoverDates.has(dateString);
          const showHighlight = inRange || inHoverRange;
          const isRangeStart = isStartDate && showHighlight;
          const isRangeEnd = isEndDate && inRange;
          const isHoverEnd = inHoverRange && dateString === hoverDate;

          let highlightSx = {};
          if (showHighlight) {
            const bg = inRange ? 'rgba(255,110,0,0.3)' : 'rgba(255,110,0,0.15)';
            const band = {
              content: '""',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              height: CELL_SIZE - 2,
              zIndex: 0,
            };
            const isAnchorStart = isRangeStart || (isStartDate && inHoverRange);
            const isAnchorEnd = isRangeEnd || isHoverEnd;
            if (isAnchorStart && isAnchorEnd) {
              highlightSx = {};
            } else if (isAnchorStart) {
              const side = inHoverRange && hoverBefore ? { left: 0, right: '50%' } : { left: '50%', right: 0 };
              highlightSx = { '&::after': { ...band, ...side, backgroundColor: bg } };
            } else if (isAnchorEnd) {
              const side = inHoverRange && hoverBefore ? { left: '50%', right: 0 } : { left: 0, right: '50%' };
              highlightSx = { '&::after': { ...band, ...side, backgroundColor: bg } };
            } else {
              highlightSx = { '&::after': { ...band, left: 0, right: 0, backgroundColor: bg } };
            }
          }

          const rangeHandlers = onRangeSelect ? range.getHandlers(dateString, isClickable) : {};

          const handleMouseEnter = isClickable
            ? () => {
                if (onRangeSelect) {
                  rangeHandlers.handleMouseEnter?.();
                } else {
                  setHoverDateSimple(dateString);
                }
              }
            : undefined;

          const handleClick = isClickable
            ? () => {
                if (onRangeSelect) {
                  rangeHandlers.handleClick?.();
                  return;
                }
                onSelectDate(isStartDate ? null : dateString);
              }
            : undefined;

          const cell = (
            <CellOuter
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-label={isClickable ? format(day, 'MMMM d, yyyy') : undefined}
              isClickable={isClickable}
              onClick={handleClick}
              onMouseDown={rangeHandlers.handleMouseDown}
              onMouseUp={rangeHandlers.handleMouseUp}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => (onRangeSelect ? range.clearHover() : setHoverDateSimple(null))}
              onKeyDown={
                isClickable && !onRangeSelect ? e => e.key === 'Enter' && onSelectDate(isStartDate ? null : dateString) : undefined
              }
              sx={highlightSx}
            >
              {circle}
            </CellOuter>
          );

          const tooltipText = inMonth && !isPast && hasData && !isLoading ? getTooltipText(status) : '';
          return (
            <Tooltip key={dateString} title={tooltipText}>
              {cell}
            </Tooltip>
          );
        })}
      </DayGrid>
    </Box>
  );
}
